/**
 * The full answer of ONE turn — the answer only, not the conversation (§5.13). Opened from
 * the truncated answer, « Voir la réponse complète » or the « +N sources » chip.
 *
 * Sources are listed positionally (index + 1 = the inline `[n]` marker), built with `map`,
 * never filtered. A link is emitted only for an http(s) URL, and never for a Gemini
 * grounding-redirect wrapper's opaque path.
 */

import { Box, Dialog, DialogContent, DialogTitle, IconButton, Link, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import type { ConversationTurn } from '@traaker/shared';
import { HighlightedResponse } from '../../common/HighlightedResponse';
import { EngineLogo } from '../../brand/EngineLogo';
import DomainFavicon from '../../competitors/DomainFavicon';
import { sourceHref, isGroundingWrapper } from '../../../utils/sourceUrl';
import { engineDisplay, engineLogoId } from '../personaFormat';
import { BUCKET_COLOR, bucketOf, isOwnSource, ownSourceHighlights, sourceDomainAt } from './conversationView';
import { highlightsOf } from './turnText';

export function ResponseDialog({
  open,
  onClose,
  turn,
  engine,
  brandId,
}: {
  open: boolean;
  onClose: () => void;
  turn: ConversationTurn | null;
  engine: string;
  brandId?: string;
}) {
  const { t } = useTranslation();
  if (!turn) return null;
  const bucket = bucketOf(turn.promptIntent);
  const highlights = highlightsOf(turn.targetMatches);
  const n = (turn.sourceLinks?.length || turn.sources?.length) ?? 0;
  const rows = Array.from({ length: n }, (_, i) => {
    const link = (turn.sourceLinks ?? [])[i] || '';
    const label = (turn.sources ?? [])[i] || '';
    return { i, domain: sourceDomainAt(turn, i), href: sourceHref(link || label), url: isGroundingWrapper(link) ? '' : link || label, own: isOwnSource(turn, i) };
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6, borderBottom: '1px solid', borderColor: 'divider' }}>
        <EngineLogo engine={engineLogoId(engine)} size={20} />
        <Typography component="span" fontWeight={700}>
          {t('conversations.view.responseTitle', { engine: engineDisplay(engine), n: turn.turn })}
        </Typography>
        {bucket && (
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.6, ml: 0.5, fontSize: 13, color: 'text.secondary' }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: BUCKET_COLOR[bucket] }} />
            {t(`conversations.view.bucket_${bucket}`)}
          </Box>
        )}
        <IconButton aria-label={t('common.close')} onClick={onClose} sx={{ position: 'absolute', right: 10, top: 10 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: '16px !important' }}>
        <Box sx={{ maxWidth: '68ch', '& .MuiTypography-body2, & .MuiTypography-body1': { fontSize: '14.5px', lineHeight: 1.7 } }}>
          <HighlightedResponse
            text={turn.engineResponse ?? ''}
            brandId={brandId}
            sources={turn.sources}
            sourceLinks={turn.sourceLinks}
            targetHighlights={highlights.prose}
            targetSourceHighlights={ownSourceHighlights(turn, highlights.sources)}
          />
        </Box>
        {n > 0 && (
          <>
            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mt: 2.5, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              {t('conversations.view.responseSources', { count: n })}
            </Typography>
            <Box component="ol" sx={{ listStyle: 'none', p: 0, m: 0, mt: 1 }}>
              {rows.map((r) => (
                <Box component="li" key={r.i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Box sx={{ flex: '0 0 18px', fontSize: 11, fontWeight: 700, color: 'text.disabled', textAlign: 'right', pt: '2px' }}>{r.i + 1}</Box>
                  <DomainFavicon domain={r.domain ?? ''} name={r.domain ?? ''} size={17} />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {r.href ? (
                        <Link href={r.href} target="_blank" rel="noopener noreferrer" sx={{ fontSize: 13, fontWeight: 600, color: r.own ? 'primary.dark' : 'text.primary' }}>
                          {r.domain ?? r.url}
                        </Link>
                      ) : (
                        <Typography component="span" sx={{ fontSize: 13, fontWeight: 600 }}>{r.domain ?? r.url}</Typography>
                      )}
                      {r.own && (
                        <Box component="span" sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: 'primary.dark', bgcolor: 'rgba(124,58,237,0.08)', borderRadius: '4px', px: 0.6 }}>
                          {t('conversations.view.you')}
                        </Box>
                      )}
                    </Box>
                    {r.url && (
                      <Typography sx={{ fontSize: 11.5, color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.url}</Typography>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
